import { useState, useEffect, createRef } from 'react'
import { jobs } from '../../../data/jobsData_2019'
import { scaleLinear } from 'd3-scale'
import { min, max } from 'd3-array'
import { line } from 'd3-shape'
import '../../../styles/jobs.scss'
import { useDimensionsContext } from '../Chart'
import { XAxis, YAxis } from './Axes'

const Jobs = ({ step, direction }) => {
  let dimensions = useDimensionsContext()

  const isShort = dimensions.height < 300
  const isNarrow = dimensions.width < 450

  const x = scaleLinear(
    [jobs[0][0].year, jobs[0][jobs[0].length - 1].year],
    [0, dimensions.boundedWidth]
  )
  const minJobs = min(jobs.flat().map((d) => d.job_creation))
  const maxJobs = max(jobs.flat().map((d) => d.job_creation))
  const y = scaleLinear(
    [minJobs - 2000000, maxJobs + 2500000],
    [dimensions.boundedHeight, 0]
  )

  const lineGenerator = line()
    .x((d) => x(d.year))
    .y((d) => y(d.job_creation))

  //data for 1992-2007
  const jobsPartialOne = jobs[1]
    .map((item) => (item.year <= 2007 ? item : null))
    .filter((item) => item)

  // data for 2007-2018 -- uses original line generator
  const jobsPartialTwo = jobs[1]
    .map((item) => (item.year >= 2007 ? item : null))
    .filter((item) => item)

  const findDotPos = (year) => jobs[1].find((d) => d.year === year)

  const pathRef = createRef()
  const [pathLength, setPathLength] = useState()

  useEffect(() => {
    setPathLength(pathRef.current.getTotalLength())
  }, [pathRef])

  return (
    <>
      <defs>
        <linearGradient id="grad1" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" className="jobs-bg-grad-start" />
          <stop offset="100%" className="jobs-bg-grad-end" />
        </linearGradient>
        <pattern
          id="stripes"
          patternUnits="userSpaceOnUse"
          width="10"
          height="40"
          patternTransform="rotate(45 0 0)"
        >
          <line id="stripe" className="jobs-pattern" y2="40" />
        </pattern>
      </defs>
      <g>
        <XAxis x={x} />
        <YAxis y={y} />
        <rect
          fill="url(#grad1)"
          width={dimensions.boundedWidth}
          height={y(0)}
        />
        <rect
          x={x(2007)}
          width={x(2009) - x(2007)}
          height={dimensions.boundedHeight}
          fill="url(#stripes)"
        />
      </g>
      <g>
        <path
          className={`job-line bottom step-${step}`}
          ref={pathRef}
          strokeDasharray={step === 0 ? pathLength : null}
          strokeDashoffset={step === 0 ? pathLength : null}
          d={lineGenerator(jobs[0])}
        />
        <text
          className={`job-label bottom step-${step} ${direction}`}
          x={x(jobs[0][22].year)}
          y={y(-1500000)}
        >
          Established
        </text>
        <text
          className={`job-label bottom step-${step} ${direction}`}
          x={x(jobs[0][22].year)}
          y={y(-1500000) + 20}
        >
          firm jobs
        </text>
        <text
          className={`job-label bottom step-${step} detail ${direction}`}
          x={x(2008)}
          y={y(jobs[0].find((d) => d.year === 2009).job_creation) + 20}
        >
          2007-2009
        </text>
        <text
          className={`job-label bottom step-${step} detail ${direction}`}
          x={x(2008)}
          y={y(jobs[0].find((d) => d.year === 2009).job_creation) + 38}
        >
          The Great Recession
        </text>
      </g>
      <g>
        <path
          className={`job-line top-1 step-${step}`}
          d={lineGenerator(jobsPartialOne)}
        />
        <path
          className={`job-line top-2 step-${step} ${direction}`}
          d={lineGenerator(jobsPartialTwo)}
        />
        <circle
          className={`dot-marker step-${step} first`}
          cx={x(1992)}
          cy={y(findDotPos(1992).job_creation)}
          r={6}
        />
        <text
          x={x(1992)}
          y={y(findDotPos(1992).job_creation) + 30}
          className={`dot-label step-${step} first`}
        >
          1992
        </text>
        <circle
          className={`dot-marker step-${step} second ${direction}`}
          cx={x(step <= 1 ? 2007 : 2009)}
          cy={y(findDotPos(step <= 1 ? 2007 : 2009).job_creation)}
          r={6}
        />
        <text
          x={x(step <= 1 ? 2007 : 2009)}
          y={y(findDotPos(step <= 1 ? 2007 : 2009).job_creation) + 30}
          className={`dot-label step-${step} second`}
        >
          {step <= 1 ? 2007 : 2009}
        </text>
        <circle
          className={`dot-marker step-${step}`}
          cx={x(jobs[1][jobs[1].length - 1].year)}
          cy={y(jobs[1][jobs[1].length - 1].job_creation)}
          r={6}
        />
        <text
          x={x(jobs[1][jobs[1].length - 1].year)}
          y={y(jobs[1][jobs[1].length - 1].job_creation) + 30}
          className={`dot-label step-${step}`}
        >
          {jobs[1][jobs[1].length - 1].year}
        </text>
        <text
          x={0.01 * dimensions.boundedWidth}
          y={25}
          className={`job-label top step-${step} text`}
        >
          {`Nearly 2.8M new jobs on average ${isShort ? 'from start ups' : ''}`}
        </text>
        {!isShort && (
          <text
            x={0.01 * dimensions.boundedWidth}
            y={isNarrow ? 42 : 48}
            className={`job-label top step-${step} text`}
          >
            from start ups
          </text>
        )}
        <text
          x={x(2007) + 12}
          y={y(findDotPos(2007).job_creation)}
          className={`job-label top step-${step} first`}
          dy=".35em"
        >
          Start-up jobs
        </text>
        <text
          x={x(isNarrow ? 2012 : 2015)}
          y={y(isShort ? 3500000 : 3000000)}
          className={`job-label top step-${step} second ${direction}`}
          dy=".35em"
        >
          Start-up jobs
        </text>
      </g>
    </>
  )
}

export default Jobs
